import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Phone, Loader2, Search, CheckCircle2, X, MapPin } from "lucide-react";
import { apiClient, formatApiError } from "../../lib/auth";

const digits = (s) => (s || "").replace(/\D/g, "");

export default function NumberSetup({ open, onClose, onActivated, defaultCell = "", defaultAreaCode = "" }) {
  const [areaCode, setAreaCode] = useState(defaultAreaCode);
  const [numbers, setNumbers] = useState([]);
  const [selected, setSelected] = useState("");
  const [cell, setCell] = useState(defaultCell);
  const [searching, setSearching] = useState(false);
  const [buying, setBuying] = useState(false);
  const [error, setError] = useState("");

  const search = useCallback(async (ac) => {
    setSearching(true); setError(""); setNumbers([]); setSelected("");
    try {
      const q = ac && ac.length === 3 ? `?area_code=${ac}` : "";
      const { data } = await apiClient.get(`/voice/numbers/available${q}`);
      setNumbers(data.numbers || []);
      if ((data.numbers || []).length) setSelected(data.numbers[0].phone_number);
      if (!(data.numbers || []).length) setError(ac ? `No numbers available for area code ${ac}. Try a nearby one.` : "No numbers available right now. Try again shortly.");
    } catch (e) { setError(formatApiError(e, "Couldn't search numbers. Try again.")); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    if (open) { setAreaCode(defaultAreaCode); setCell(defaultCell); search(defaultAreaCode); }
  }, [open]); // eslint-disable-line

  const activate = async () => {
    if (!selected) { setError("Pick a number from the list."); return; }
    if (digits(cell).length < 10) { setError("Enter your cell number so calls can reach you."); return; }
    setBuying(true); setError("");
    try {
      const { data } = await apiClient.post("/voice/numbers/purchase", { phone_number: selected, personal_phone: cell });
      onActivated?.(data);
    } catch (e) { setError(formatApiError(e, "Couldn't activate that number. Try another.")); }
    finally { setBuying(false); }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="number-setup-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-md" onClick={onClose} />
      <div className="relative bg-white border border-slate-200 rounded-3xl w-full max-w-lg shadow-[0_30px_80px_-20px_rgba(15,23,42,0.35)] overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <button data-testid="number-setup-close" onClick={onClose} className="absolute top-5 right-5 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          <div className="w-11 h-11 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-3"><Phone className="w-5 h-5 text-emerald-600" /></div>
          <h3 className="font-display text-xl font-bold text-slate-900">Choose your calling number</h3>
          <p className="text-sm text-slate-500 mt-1">Pick a local number by area code. Leads text and call this number; everything lands in your CRM.</p>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Area code</label>
            <div className="flex gap-2">
              <input data-testid="number-area-code" value={areaCode} onChange={(e) => setAreaCode(digits(e.target.value).slice(0, 3))}
                onKeyDown={(e) => { if (e.key === "Enter") search(areaCode); }} placeholder="e.g. 305" inputMode="numeric"
                className="w-28 bg-slate-50 border border-slate-200 focus:border-[#1D6AFF] focus:bg-white outline-none rounded-xl px-3.5 py-2.5 text-sm transition-all" />
              <button data-testid="number-search-btn" onClick={() => search(areaCode)} disabled={searching}
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold rounded-xl px-4 py-2.5 text-sm transition-all">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Available numbers</label>
            {searching ? (
              <div className="flex items-center justify-center py-8 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Finding numbers…</div>
            ) : (
              <div className="space-y-1.5" data-testid="number-list">
                {numbers.map((n) => (
                  <button key={n.phone_number} data-testid={`number-option-${digits(n.phone_number)}`} onClick={() => setSelected(n.phone_number)}
                    className={`w-full flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl border text-left transition-all ${selected === n.phone_number ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"}`}>
                    <span className="font-semibold text-sm text-slate-900 tabular-nums">{n.phone_number}</span>
                    <span className="text-xs text-slate-500 flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" />{n.locality || n.region || "US"}{n.region ? `, ${n.region}` : ""}</span>
                    {selected === n.phone_number && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                  </button>
                ))}
                {!numbers.length && !error && <div className="text-sm text-slate-400 py-4 text-center">Search an area code to see numbers.</div>}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Your cell number</label>
            <input data-testid="number-cell-input" type="tel" value={cell} onChange={(e) => setCell(e.target.value)} placeholder="(305) 555-0123"
              className="w-full bg-slate-50 border border-slate-200 focus:border-[#1D6AFF] focus:bg-white outline-none rounded-xl px-3.5 py-2.5 text-sm transition-all" />
            <p className="text-[11px] text-slate-400 mt-1">Used so inbound calls ring you, and for the "ring my cell" calling option.</p>
            <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-testid="number-sms-consent">
              By activating, you agree to receive calls &amp; texts from Newground at this number (new-lead alerts &amp; account notices).
              Message frequency varies; message &amp; data rates may apply. Reply STOP to opt out, HELP for help. See our{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1D6AFF] hover:underline">Privacy Policy</a> and{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#1D6AFF] hover:underline">Terms</a>.
            </p>
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="number-setup-error">{error}</div>}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
          <p className="text-[11px] text-slate-400">Buying a number costs ~$1/mo + usage via your Twilio account.</p>
          <button data-testid="number-activate-btn" onClick={activate} disabled={buying || !selected}
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold rounded-xl px-5 py-2.5 text-sm transition-all shadow-sm whitespace-nowrap">
            {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {buying ? "Activating…" : "Activate this number"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
